<template lang="html">
  <div class="dib user-select-none ph4 ph1-ns">
    <div v-if="title" class="b mb3">{{ title }}</div>
    <div class="mb3">
      <span>
        <span class="f4 font-futura-bold">{{ minLabel }}</span>
        <span class="b ttu tracked f7">{{ unitLabel }}</span>
      </span>
      <span class="mh2">-</span>
      <span>
        <span class="f4 font-futura-bold">{{ maxLabel }}</span>
        <span class="b ttu tracked f7">{{ unitLabel }}</span>
      </span>
    </div>
    <div class="slider-container relative"
      :style="{ 'height': (2 * margin) + 'px' }"
      @click="snapNearestControl($event)"
      ref="slider"
    >
      <div class="control pointer absolute
      top-0 br-pill ba b--action bg-white z-5"
        :style="{
          'left': (min.pos + margin) + 'px',
          'height': (2 * margin) + 'px',
          'width': (2 * margin) + 'px'
        }"
        @mousedown="moveStart($event, 0)"
        @touchstart="moveStart($event, 0)"
        @click="$event.preventDefault()"
      />
      <div class="control pointer absolute
      top-0 br-pill ba b--action bg-white z-5"
        :style="{
          'left': (max.pos + margin) + 'px',
          'height': (2 * margin) + 'px',
          'width': (2 * margin) + 'px'
        }"
        @mousedown="moveStart($event, 1)"
        @touchstart="moveStart($event, 1)"
        @click="$event.preventDefault()"
      />
      <div class="slider relative w-100 bg-gray z-3"
        :style="{ 'width': (width + margin * 2) + 'px' }"
      />
      <div class="slider absolute bg-action z-4"
        :style="{
          'width': posDifference + 'px',
          'left':  (min.pos + margin) + 'px'
        }"
      />
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: {
      type: String,
      default: ''
    },
    defaultRange: {
      type: Object,
      required: true,
      default: () => ({
        min: 0,
        max: 9999
      })
    },
    range: {
      type: Object,
      required: true,
      default: () => Object.assign({}, this.defaultRange)
    },
    unitLabel: {
      type: String,
      default: ''
    },
    width: {
      type: Number,
      default: 200
    },
    margin: {
      type: Number,
      default: 14
    }
  },
  data() {
    return {
      startX: 0,
      min: {
        drag: false,
        pos: 0
      },
      max: {
        drag: false,
        pos: this.width
      }
    };
  },
  created() {
    this.min.pos = this.convertRangeToPos(this.range.min);
    this.max.pos = this.convertRangeToPos(this.range.max);
  },
  computed: {
    minLabel() {
      return this.convertPosToRange(this.min.pos);
    },
    maxLabel() {
      return this.convertPosToRange(this.max.pos);
    },
    rangeDifference() {
      return Math.abs(this.defaultRange.max - this.defaultRange.min);
    },
    posDifference() {
      return Math.abs(this.max.pos - this.min.pos);
    }
  },
  watch: {
    range(newRange) {
      this.min.pos = this.convertRangeToPos(newRange.min);
      this.max.pos = this.convertRangeToPos(newRange.max);
    }
  },
  methods: {
    convertPosToRange(pos) {
      return Math.floor(
        this.defaultRange.min
        + ((this.rangeDifference / this.width) * pos)
      );
    },
    convertRangeToPos(range) {
      return (
        (this.width / this.rangeDifference)
        * (range - this.defaultRange.min)
      );
    },
    moveStart(e, index = 0) {
      let pointer = e;
      if ('touches' in e) pointer = e.touches[0];
      if (index === 0) this.min.drag = true;
      else if (index === 1) this.max.drag = true;
      this.startX = pointer.clientX;
      document.addEventListener('mousemove', this.move);
      document.addEventListener('mouseup', this.moveEnd);
      document.addEventListener('touchmove', this.move);
      document.addEventListener('touchend', this.moveEnd);
    },
    move(e) {
      let pointer = e;
      if ('touches' in e) pointer = e.touches[0];

      const movementX = pointer.clientX - this.startX;

      if (this.min.drag) {
        if ((this.min.pos + movementX) < 0) {
          this.min.pos = 0;
        } else if ((this.min.pos + movementX) > this.max.pos) {
          this.min.pos = this.max.pos;
        } else {
          this.min.pos += movementX;
          this.startX = pointer.clientX;
        }
      } else if (this.max.drag) {
        if ((this.max.pos + movementX) < this.min.pos) {
          this.max.pos = this.min.pos;
        } else if ((this.max.pos + movementX) > this.width) {
          this.max.pos = this.width;
        } else {
          this.max.pos += movementX;
          this.startX = pointer.clientX;
        }
      }
    },
    moveEnd() {
      this.min.drag = false;
      this.max.drag = false;
      document.removeEventListener('mousemove', this.move);
      document.removeEventListener('mouseup', this.moveEnd);
      document.removeEventListener('touchmove', this.move);
      document.removeEventListener('touchend', this.moveEnd);
      this.updateRange();
    },
    snapNearestControl(e) {
      const sliderClientX =
        Math.floor(this.$refs.slider.getBoundingClientRect().left);
      const offsetX = e.clientX - sliderClientX - this.margin;
      if (offsetX < 0) {
        this.min.pos = 0;
      } else if (offsetX > this.width) {
        this.max.pos = this.width;
      } else if (Math.abs(offsetX - this.max.pos)
        < Math.abs(offsetX - this.min.pos)) {
        this.max.pos = offsetX;
        this.updateRange();
      } else {
        this.min.pos = offsetX;
        this.updateRange();
      }
    },
    updateRange() {
      this.$emit('change', {
        min: this.convertPosToRange(this.min.pos),
        max: this.convertPosToRange(this.max.pos)
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.control {
  transform: translateX(-50%);
}

.slider {
  height: 2px;
  top: 50%;
  transform: translateY(-1px);
}
</style>



// WEBPACK FOOTER //
// src/components/RangeSlider.vue